/**
 * API client for the performance report (/performance/*).
 *
 * Mirrors backend/schemas.py's Performance* models. The three outcome counts
 * are carried separately and are never added together — see the backend's
 * queries/performance.py for why a flag disappearing is not the same as
 * someone fixing it.
 */
import { API } from "../config/api"

export interface PerformanceTotals {
  fixed: number
  left_inventory: number
  reclassified: number
}

export interface PerformanceDay {
  day: string
  fixed: number
  left_inventory: number
  reclassified: number
}

export interface PerformanceProblem {
  problem_type: string
  label: string
  severity: string
  fixed: number
  median_open_minutes: number | null
}

export interface PerformanceVehicle {
  vin: string
  stock: string | null
  year: string | null
  make: string | null
  model: string | null
  rooftop: string | null
  problem_type: string
  label: string
  outcome: string
  open_minutes: number | null
  /** When the flag first appeared; null where first-seen was never recorded. */
  flagged_at: string | null
  resolved_at: string
  /** What the operator wrote about the car while the flag was open, newest
   * first. The audit knows a problem stopped being flagged, never how. */
  notes: string[]
  /** Who ticked "worked" before it resolved; null means nobody claimed it. */
  worked_by: string | null
}

export interface PerformanceReport {
  start: string
  end: string
  totals: PerformanceTotals
  /** Fixes carrying a worked mark — what a person actually resolved. */
  resolved_by_people: number
  /** Fixes nobody claimed — feeds catching up. Only used by the empty state. */
  cleared_automatically: number
  median_open_minutes: number | null
  longest_open_minutes: number | null
  duplicates_collapsed: number
  by_day: PerformanceDay[]
  by_problem: PerformanceProblem[]
  vehicles: PerformanceVehicle[]
}

const TOKEN_KEY = "invi_token"

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY)
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
  return res.json()
}

function windowParams(
  start: string, end: string, dealerGroupId: number,
): string {
  return `?start=${start}&end=${end}&dealer_group_id=${dealerGroupId}`
}

export async function getPerformanceReport(
  start: string, end: string, dealerGroupId: number,
): Promise<PerformanceReport> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.performanceReport}${
      windowParams(start, end, dealerGroupId)
    }`,
    { headers: authHeaders() }
  )
  return handleResponse<PerformanceReport>(res)
}

export async function exportPerformanceWorkbook(
  start: string, end: string, dealerGroupId: number,
): Promise<Blob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.performanceReportXlsx}${
      windowParams(start, end, dealerGroupId)
    }`,
    { headers: authHeaders() }
  )
  if (!res.ok) {
    let detail = `Download failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with the generic message
    }
    throw new Error(detail)
  }
  return res.blob()
}

/**
 * Human-scale duration, matching the workbook's `format_open_time` exactly.
 * Coarse on purpose: audits run about once a day, so the true moment of a fix
 * sits inside the gap between two runs — "2d" claims as much precision as the
 * measurement supports, where "2d 19h 6m" would claim far more.
 */
export function formatOpenTime(minutes: number | null): string {
  if (minutes === null) return "—"
  if (minutes < 60) return `${minutes}m`
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  return hours === 0 ? `${days}d` : `${days}d ${hours}h`
}
